import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useRevealAll } from "../hooks/useReveal";
import { useSeo, breadcrumbLd, SITE_NAME } from "../hooks/useSeo";
import { BLOG_POSTS } from "../data/blogPosts";

export default function Blog() {
  const ref = useRef(null);
  useRevealAll(ref);
  useSeo({
    title: `Blog – póthaj és hajhosszabbítás útmutatók | ${SITE_NAME}`,
    description:
      "Szakmai útmutatók festetlen európai és kazahsztáni póthajról, hajhosszabbításról és póthaj ápolásról. Válaszd ki a hajadhoz illő prémium hajat.",
    path: "/blog",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Blog",
          name: `${SITE_NAME} Blog`,
          inLanguage: "hu-HU",
          url: window.location.origin + "/blog",
          blogPost: BLOG_POSTS.map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            url: `${window.location.origin}/blog/${p.slug}`,
            datePublished: p.datePublished,
            image: p.image,
          })),
        },
        breadcrumbLd([["Kezdőlap", "/"], ["Blog", "/blog"]]),
      ],
    },
  });

  return (
    <main ref={ref} data-testid="page-blog" className="pt-28 md:pt-32 bg-[color:var(--he-bg)]">
      <section className="he-container pt-12 pb-16 md:pb-24 border-b border-[color:var(--he-line)]">
        <p className="overline mb-5 mag-marker reveal">
          <span>Póthaj blog · Útmutatók</span>
        </p>
        <h1
          className="font-serif-display tracking-[-0.022em] reveal delay-1"
          style={{ fontWeight: 400, fontSize: "clamp(2.6rem, 6vw, 5.2rem)", lineHeight: 1.05 }}
        >
          A haj <span className="italic text-[color:var(--he-gold)]">naplója.</span>
        </h1>
        <p className="mt-8 max-w-2xl text-[color:var(--he-ink-soft)] font-light leading-[1.85] reveal delay-2">
          Útmutatók a prémium póthaj kiválasztásához: festetlen európai és
          kazahsztáni haj, egyenes, hullámos és göndör textúrák, valamint a
          hajhosszabbítás utáni póthaj ápolás.
        </p>
      </section>

      <section className="he-container py-20 md:py-28 grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-16">
        {BLOG_POSTS.map((post, i) => (
          <PostCard key={post.slug} post={post} index={i} />
        ))}
      </section>
    </main>
  );
}

function PostCard({ post, index }) {
  return (
    <article className={`reveal delay-${index + 1} group`} data-testid={`blog-card-${post.slug}`}>
      <Link to={`/blog/${post.slug}`} className="block" data-testid={`blog-card-link-${index}`}>
        <div className="overflow-hidden aspect-[4/5] bg-[color:var(--he-line)]">
          <img
            src={post.image}
            alt={post.imageAlt}
            loading={index === 0 ? "eager" : "lazy"}
            width="1200"
            height="1500"
            className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
          />
        </div>
        <div className="mt-8 flex items-center gap-4 overline-xs text-[color:var(--he-ink-soft)]">
          <span className="text-[color:var(--he-gold)]">{post.category}</span>
          <span className="w-6 h-px bg-[color:var(--he-line)]" aria-hidden />
          <span>{post.readTime} olvasás</span>
        </div>
        <h2
          className="mt-4 font-serif-display tracking-[-0.018em]"
          style={{ fontWeight: 400, fontSize: "clamp(1.6rem, 2.4vw, 2.1rem)", lineHeight: 1.18 }}
        >
          {post.title}
        </h2>
        <p className="mt-4 text-[color:var(--he-ink-soft)] font-light leading-[1.8]">{post.excerpt}</p>
        <span className="mt-6 inline-flex items-center gap-2 overline-xs he-link">
          Tovább olvasom <ArrowUpRight size={13} />
        </span>
      </Link>
    </article>
  );
}
